import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field, PageHeader, StatusBadge } from "@/components/shared/Common";
import { FilesPanel, StatusTimeline } from "@/components/shared/Records";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { OT_TONE, clp, fmtDate, fmtDateTime } from "@/lib/format";
import { Pick } from "./Sales";

const Info = ({ label, children }) => (
  <div><div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#8a9584]">{label}</div><div className="text-sm mt-0.5">{children || "—"}</div></div>
);

const Summary = ({ o }) => {
  const days = (a, b) => Math.round((new Date(b) - new Date(a)) / 86400000);
  const today = new Date().toISOString().slice(0, 10);
  return (
    <div className="grid lg:grid-cols-[1fr_320px] gap-6">
      <div className="syc-card p-6 grid sm:grid-cols-2 gap-5" data-testid="ot-summary">
        <Info label="Cliente / Mandante">{o.client_name}</Info><Info label="Proyecto / Obra">{o.project}</Info>
        <Info label="Centro de costo">{o.cost_center_name}</Info><Info label="Responsable">{o.manager_name}</Info>
        <Info label="Ubicación">{o.location}</Info><Info label="Dirección">{o.address}</Info>
        <Info label="Inicio">{fmtDate(o.start_date)}</Info><Info label="Término">{fmtDate(o.end_date)}</Info>
        <Info label="Venta de origen">{o.opportunity ? <Link className="text-[#258F00] font-semibold" to={`/gerencia/ventas/${o.opportunity.id}`}>{o.opportunity.code}</Link> : "Creada directamente"}</Info>
        <Info label="Creada por">{o.created_by_name} · {fmtDateTime(o.created_at)}</Info>
        <div className="sm:col-span-2"><Info label="Descripción">{o.description}</Info></div>
        <div className="sm:col-span-2"><Info label="Responsables principales">{o.responsibles.map((r) => `${r.name} (${r.role})`).join(" · ")}</Info></div>
        <div className="sm:col-span-2"><Info label="Observaciones">{o.notes}</Info></div>
      </div>
      <div className="space-y-4">
        <div className="syc-card p-5 space-y-3" data-testid="ot-plazo">
          <div className="font-semibold text-sm">Plazo</div>
          <div className="text-sm">Duración: <b>{days(o.start_date, o.end_date) + 1} días</b></div>
          <div className="text-sm">Transcurridos: <b>{Math.max(0, Math.min(days(o.start_date, today), days(o.start_date, o.end_date)))}</b> · Restantes: <b>{Math.max(0, days(today, o.end_date))}</b></div>
          {o.overdue && <StatusBadge status="bloqueado" label="OT atrasada" testid="ot-overdue-badge" />}
        </div>
        {o.can_see_economics && (
          <div className="syc-card p-5 space-y-2" data-testid="ot-economics">
            <div className="font-semibold text-sm">Económico</div>
            <div className="text-sm flex justify-between"><span>Valor adjudicado</span><b>{clp(o.awarded_value)}</b></div>
            <div className="text-sm flex justify-between"><span>Presupuesto</span><b>{clp(o.budget)}</b></div>
            <div className="text-sm flex justify-between"><span>Margen presupuestado</span><b>{clp(o.awarded_value - o.budget)}</b></div>
            <p className="text-xs text-[#5b6656]">El costo real y el margen se calcularán desde movimientos trazables de RR.HH., Abastecimiento, Compras y Flota.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default function OtDetail() {
  const { id } = useParams();
  const { can } = useAuth();
  const [o, setO] = useState(null);
  const [states, setStates] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [target, setTarget] = useState("");
  const [reason, setReason] = useState("");
  const load = useCallback(() => api.get(`/gerencia/ots/${id}`).then((r) => setO(r.data)).catch((e) => toast.error(errMsg(e))), [id]);
  useEffect(() => { load(); api.get("/catalogs/estados_ot").then((r) => setStates(r.data)); }, [load]);
  if (!o) return <div className="text-[#5b6656]">Cargando…</div>;

  const final = ["CERRADA", "CANCELADA"].includes(o.status);
  const allowed = states.filter((s) => s.code !== o.status && (s.code === "CERRADA" ? can("gerencia.ot.cerrar") : s.code === "CANCELADA" ? can("gerencia.ot.cancelar") : can("gerencia.ot.editar")));
  const change = async () => {
    try { await api.post(`/gerencia/ots/${id}/status`, { status: target, reason }); toast.success("Estado actualizado"); setDlg(false); setReason(""); setTarget(""); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="ot-detail-page">
      <Link to="/gerencia/ot" className="inline-flex items-center gap-1 text-sm font-semibold text-[#258F00] mb-4" data-testid="back-to-ots-link"><ArrowLeft size={15} />Órdenes de Trabajo</Link>
      <PageHeader eyebrow={<span className="flex items-center gap-2 font-mono">{o.code} <StatusBadge testid="ot-status-badge" status={OT_TONE[o.status] || "info"} label={o.status} /></span>} title={o.title} subtitle={`${o.client_name} · ${o.project}`}
        actions={!final && <>
          {can("gerencia.ot.editar") && <Link to={`/gerencia/ot/${id}/editar`}><Button data-testid="edit-ot-btn" variant="outline" className="rounded-full"><Pencil size={14} className="mr-1" />Editar</Button></Link>}
          {allowed.length > 0 && <Button data-testid="change-ot-status-btn" className="rounded-full" onClick={() => setDlg(true)}>Cambiar estado</Button>}
        </>} />
      <Tabs defaultValue="resumen">
        <TabsList className="mb-5 flex-wrap h-auto">
          <TabsTrigger value="resumen" data-testid="ot-tab-summary">Resumen</TabsTrigger>
          <TabsTrigger value="documentos" data-testid="ot-tab-documents">Documentos</TabsTrigger>
          <TabsTrigger value="historial" data-testid="ot-tab-history">Historial</TabsTrigger>
        </TabsList>
        <TabsContent value="resumen"><Summary o={o} /></TabsContent>
        <TabsContent value="documentos"><FilesPanel resourceType="ot" resourceId={id} canWrite={can("gerencia.documentos.gestionar") && !final} /></TabsContent>
        <TabsContent value="historial"><StatusTimeline items={o.status_history} /></TabsContent>
      </Tabs>
      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent data-testid="ot-status-dialog">
          <DialogHeader><DialogTitle>Cambiar estado de {o.code}</DialogTitle></DialogHeader>
          <Field label="Nuevo estado"><Pick testid="ot-status-select" value={target} onChange={setTarget} options={allowed.map((s) => ({ id: s.code, name: s.name }))} placeholder="Seleccione" /></Field>
          {target === "CERRADA" && <div className="rounded-lg border border-[#FFE100] bg-[#fffbe0] p-3 text-xs" data-testid="ot-close-warning">Antes del cierre se verificarán herramientas, sub-bodegas y transferencias pendientes (disponible con Abastecimiento, Fase 4). El cierre es definitivo y queda auditado.</div>}
          <Field label={["CERRADA", "CANCELADA"].includes(target) ? "Motivo *" : "Observación"}><Textarea data-testid="ot-status-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
          <DialogFooter><Button data-testid="ot-status-confirm-btn" disabled={!target} className="rounded-full" onClick={change}>Confirmar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
